import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import coreAPIService from '../apiServices/coreAPIService';
import { CT_API_ENDPOINT, CTW_API_ENDPOINT } from '../utils';
import Axios from 'axios';
import applyCaseMiddleware from 'axios-case-converter'
import { hasRolePermission, normalizeRole, USER_ROLES } from '../utils/roles';
// import { frontendMonitoring, instrumentAxiosInstance } from '../monitoring';

const axios = applyCaseMiddleware(
    Axios.create({
        withCredentials: true, // Enable cookie support for future cookie-based auth
    })
)

// instrumentAxiosInstance(axios)

interface User {
    userId?: number;
    email: string;
    userName: string;
    userMode?: string;
    userType?: string;
    userRole?: string;
    userMobile?: string;
    // Legacy fields for backward compatibility
    username?: string;
    mobileNumber?: string;
    viewPiiAccess?: boolean;
    viewPii?: boolean;
}

interface Account {
    orgId: string;
    orgName: string;
    orgType?: 'parent' | 'self_served' | 'managed';
    isParent?: boolean;
    waba_numbers?: any[];
}

interface AuthOrgContextType {
    // Auth state
    isAuthenticated: boolean;
    user: User | null;

    // Org state
    currentOrg: Account | null;
    availableOrgs: Account[];

    // Computed
    userRole: string;
    canAccessSetup: boolean;

    // Actions
    login: (userData: any) => void;
    logout: () => Promise<void>;
    switchOrg: (orgId: string) => void;
    refreshSession: () => Promise<void>;
    viewPiiAccess: boolean;
    viewPii: boolean;
    updateViewPii: (enabled: boolean) => Promise<void>;
}

const AuthOrgContext = createContext<AuthOrgContextType | null>(null);

export const AuthOrgProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [currentOrg, setCurrentOrg] = useState<Account | null>(null);
    const [availableOrgs, setAvailableOrgs] = useState<Account[]>([]);
    const [loading, setLoading] = useState(true);
    const [viewPiiAccess, setViewPiiAccess] = useState(false);
    const [viewPii, setViewPii] = useState(false);

    // Check session on mount using HTTP-only cookies or localStorage
    useEffect(() => {
        checkSession();
    }, []);

    // useEffect(() => {
    //     frontendMonitoring.setIdentity({
    //         userId: user?.userId,
    //         orgId: currentOrg?.orgId,
    //     });
    // }, [user?.userId, currentOrg?.orgId]);

    const checkSession = async () => {
        try {
            // Backend verifies session via HTTP-only cookie
            // and returns user + org data if session is valid
            const token = localStorage.getItem('accessToken');
            const response = await axios.post<{
                meta: any,
                data: {
                    userDetails: User,
                    organizationDetails: Account[]
                }
            }>(CTW_API_ENDPOINT + '/v2/auth/validate-token', {}, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            });

            if (response.data && response.data.data) {
                const data = response.data.data as any;
                // Check for new format (userDetails)
                if (data.userDetails) {
                    login({
                        userDetails: data.userDetails,
                        organizationDetails: data.organizationDetails,
                        isRestoring: true
                    });
                } else {
                    // Fallback to legacy format
                    login({
                        user: data.user,
                        orgs: {
                            current: data.currentOrg,
                            available: data.availableOrgs
                        },
                        isRestoring: true
                    });
                }
            }
        } catch (error) {
            console.error('Session check error:', error);

            // Try to restore from localStorage first
            const storedUser = localStorage.getItem('user');
            const storedToken = localStorage.getItem('accessToken');

            if (storedUser && storedToken) {
                try {
                    const parsedUser = JSON.parse(storedUser);
                    setUser(parsedUser);
                    setViewPiiAccess(Boolean(parsedUser.viewPiiAccess ?? parsedUser.view_pii_access));
                    setViewPii(Boolean(parsedUser.viewPii ?? parsedUser.view_pii));
                    setIsAuthenticated(true);

                    // Fallback if no orgs stored (shouldn't happen with new login)
                    // We rely on the fact that if we are restoring from localStorage, 
                    // we might not have org details if they weren't persisted.
                    // But since user said we don't persist orgs in localStorage, 
                    // we might be in a state where we have user but no orgs if API fails.
                    // However, for now, let's just set what we have.

                    // If availableOrgs is empty, we can't set currentOrg correctly.
                    // This might be why the user was seeing a reset.
                    // But if the API call succeeds, we get the orgs.

                    return;
                } catch (e) {
                    console.error('Error parsing stored user:', e);
                }
            }

            // If we reach here, it means:
            // 1. API check failed
            // 2. LocalStorage check failed or no data found

            // console.log('No active session found');
            setIsAuthenticated(false);
            setUser(null);
            setCurrentOrg(null);
        } finally {
            setLoading(false);
        }
    };

    const login = (data: any) => {
        // Support both new format and old format
        if (data.userDetails && data.organizationDetails) {
            // New format - preferred
            // Check if we are restoring session to avoid infinite loop or redundant saves
            const isRestoring = data.isRestoring;

            const { token, userDetails, organizationDetails } = data;

            if (!isRestoring) {
                // Reverting to localStorage based auth
                if (token) {
                    localStorage.setItem('accessToken', token);
                }
                localStorage.setItem('user', JSON.stringify(userDetails));

                // Also set in state immediately
                setUser(userDetails);
                if (organizationDetails.find((org: Account) => org.isParent)) setCurrentOrg(organizationDetails.find((org: Account) => org.isParent));
                if (organizationDetails) setAvailableOrgs(organizationDetails);
                setIsAuthenticated(true);
            } else {
                // Restoring session
                setUser(userDetails);
                if (organizationDetails.find((org: Account) => org.isParent)) setCurrentOrg(organizationDetails.find((org: Account) => org.isParent));
                if (organizationDetails) setAvailableOrgs(organizationDetails);
                setIsAuthenticated(true);
            }

            // Map user details to User interface
            const user: User = {
                email: userDetails.email,
                userName: userDetails.userName,
                userMode: userDetails.userMode,
                userType: userDetails.userType,
                userRole: userDetails.userRole,
                userMobile: userDetails.userMobile,
                userId: userDetails.userId,
                viewPiiAccess: Boolean(userDetails.viewPiiAccess ?? userDetails.view_pii_access),
                viewPii: Boolean(userDetails.viewPii ?? userDetails.view_pii),
                // Legacy compatibility
                username: userDetails.userName,
            };

            // Map organizations and normalize field names
            const orgs: Account[] = organizationDetails.map((org: any) => ({
                orgId: org.organizationId,
                orgName: org.organizationName,
                isParent: org.isParent,
                // Keep original fields for reference
                organizationId: org.organizationId,
                organizationName: org.organizationName,
            }));

            // Select organization where org_id === manager_org_id
            const selectedOrg = orgs.find(org => org.isParent) || orgs[0];

            // Determine org type based on user_type
            if (selectedOrg) {
                selectedOrg.orgType = userDetails.userType === 'INTERNAL' ? 'parent' : 'self_served';
                // selectedOrg.isParent is already set from mapping
            }

            setUser(user);
            setViewPiiAccess(Boolean(user.viewPiiAccess));
            setViewPii(Boolean(user.viewPii));
            setCurrentOrg(selectedOrg);
            setAvailableOrgs(orgs);
        } else if (data.user && data.orgs) {
            // Manual/Dummy login format
            const { user, orgs } = data;
            setUser(user);
            setViewPiiAccess(Boolean(user?.viewPiiAccess ?? user?.view_pii_access));
            setViewPii(Boolean(user?.viewPii ?? user?.view_pii));
            setCurrentOrg(orgs.current);
            setAvailableOrgs(orgs.available || [orgs.current]);
        } else {
            // Old format - minimal backward compatibility
            // Just set the user, org data should be provided separately
            setUser(data);
            setViewPiiAccess(Boolean(data.viewPiiAccess ?? data.view_pii_access));
            setViewPii(Boolean(data.viewPii ?? data.view_pii));
        }
        setIsAuthenticated(true);
    };

    const logout = async () => {
        try {
            // void frontendMonitoring.syncNow({ force: true });
            // Ensure the next authenticated session starts with personal data masked.
            await coreAPIService.resetViewPiiPreference();
            // Backend will clear HTTP-only cookies
            await coreAPIService.post(CT_API_ENDPOINT, 'v2/auth/logout', {});
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Clear all context state
            setUser(null);
            setCurrentOrg(null);
            setAvailableOrgs([]);
            setViewPiiAccess(false);
            setViewPii(false);
            setIsAuthenticated(false);

            // No localStorage to clear - using HTTP-only cookies
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
        }
    };

    const switchOrg = useCallback((orgId: string) => {
        const newOrg = availableOrgs.find(org => org.orgId === orgId);
        if (!newOrg) {
            throw new Error('Account not found');
        }
        setCurrentOrg(newOrg);

        // TODO: Call backend to update session with new org
        // await coreAPIService.post(CT_API_ENDPOINT, 'auth/switch-org', { orgId });

        // Reload to refresh data with new org context
        window.location.reload();
    }, [availableOrgs]);

    const refreshSession = async () => {
        await checkSession();
    };

    const updateViewPii = async (enabled: boolean) => {
        const response = await coreAPIService.post<{ meta?: { newToken?: string | null } }>(
            CTW_API_ENDPOINT,
            'v2/admin/update-view-pii',
            { view_pii: enabled }
        );
        const newToken = response?.meta?.newToken;
        if (newToken) localStorage.setItem('accessToken', newToken);
        setViewPii(enabled);
        setUser(previous => {
            if (!previous) return previous;
            const updatedUser = { ...previous, viewPii: enabled };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            return updatedUser;
        });
    };

    const getUserRole = (): string => {
        return normalizeRole(user?.userRole || USER_ROLES.VIEWER);
    };

    const canAccessSetup = (): boolean => {
        const role = getUserRole();
        const orgType = currentOrg?.orgType;

        if (!hasRolePermission(role, 'accessSetup')) return false;
        if (orgType === 'managed') return false;

        return true;
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                Loading...
            </div>
        );
    }

    return (
        <AuthOrgContext.Provider
            value={{
                isAuthenticated,
                user,
                currentOrg,
                availableOrgs,
                userRole: getUserRole(),
                canAccessSetup: canAccessSetup(),
                login,
                logout,
                switchOrg,
                refreshSession,
                viewPiiAccess,
                viewPii,
                updateViewPii
            }}
        >
            {children}
        </AuthOrgContext.Provider>
    );
};

export const useAuthOrg = () => {
    const context = useContext(AuthOrgContext);
    if (!context) {
        throw new Error('useAuthOrg must be used within AuthOrgProvider');
    }
    return context;
};

// Backward compatibility - export old hook name
export const useAuth = useAuthOrg;
